import RevisableLanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import LayoutManager from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import Config from "../../../core/config/Config";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import ClickEaterFactory from "../../../core/ui/ClickEaterFactory";
import ScormController from "../../../scorm/controller/ScormController";
import CharacterLibrary from "../../characters/model/CharacterLibrary";
import PlayerModel from "../../player/model/PlayerModel";
import ProgressModel from "../../progress/model/ProgressModel";
import ConfettiContainer from "../../ui/ConfettiContainer";
import TrackerScore from "../../score/model/TrackerScore";
import StatusScore from "../../score/model/StatusScore";
import PdfEvent from "../../../../../lib/html5-software-library/src/ranj/pdf/PdfEvent";
import PersonalReportEvent from "../../personal-report/event/PersonalReportEvent";
import Slice9Button from "../../../../../lib/html5-software-library/src/ranj/components/scale9/Slice9Button";
import Slice9ButtonUtil from "../../util/Slice9ButtonUtil";
import Anchor from "../../../../../lib/html5-software-library/src/ranj/layout/Anchor";
import SoundType from "../../../core/sound/util/SoundType";
import SoundManager from "../../../../../lib/html5-software-library/src/ranj/audio/SoundManager";
import SystemMessageBuilder from "../../../core/system-message/controller/SystemMessageBuilder";

export default class EndOfGameScreenOverviewMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LayoutManager} layoutManager 
     * @param {ClickEaterFactory} clickEaterFactory 
     * @param {PlayerModel} playerModel
     * @param {RevisableLanguagePack} languagePack 
     * @param {Config} config 
     * @param {ScormController} scormController 
     * @param {ProgressModel} progressModel 
     * @param {CharacterLibrary} characterLibrary 
     * @param {SoundManager} soundManager 
     */
    constructor(target, dispatcher, layoutManager, clickEaterFactory, languagePack, soundManager) {
        super(target, dispatcher, layoutManager, clickEaterFactory);

        this._languagePack = languagePack;
        this._soundManager = soundManager;

        /** @type {TrackerScore[]} */
        this._trackerScores = [];
        /** @type {TrackerScore} */
        this._primaryTrackerScore = null;
        /** @type {StatusScore} */
        this._status = null;
    }

    initialize() {
        super.initialize();

        this._target.scoreMeter.initialX = this._target.scoreMeter.x;
        this._target.scoreMeter.initialY = this._target.scoreMeter.y;
        this._target.scoreMeter.initialScale = this._target.scoreMeter.scale;
        this._target.scoreMeterStart.visible = false; // Hide as we only use it for animation purposes.

        this._target.groupSpiritTextField.lineWidth = null;
        this._target.trustTextField.lineWidth = null;

        this._confettiContainer = new ConfettiContainer(this._target.confetti);
        this._confettiContainer.play();

        this._downloadPersonalReportButton = this.createIconSlice9Button(this._target.descriptivePanel.downloadPersonalReportButton, this._handleDownloadPersonalReportButton.bind(this), this._languagePack.getText(`endOfGame.overviewPanel.downloadPersonalReportButtonLabel`), SoundType.BUTTON_CLICK, Slice9Button.HORIZONTAL);

        this.registerEventListener(PdfEvent.GENERATE_REPORT_COMPLETE, this._handleDownloadPersonalReportComplete.bind(this));
        this.registerEventListener(PersonalReportEvent.ERROR, this._handleDownloadPersonalReportComplete.bind(this));

        this._setLanguagePackTexts();

        this.preventClickThrough();
    }

    destroy() {
        super.destroy();
    }

    /**
     * @param {object} params
     * @param {TrackerScore[]} params.trackerScores
     * @param {TrackerScore} params.primaryTrackerScore
     * @param {StatusScore} params.status
     */
    populate({ trackerScores, primaryTrackerScore, status }) {
        this._trackerScores = trackerScores;
        this._primaryTrackerScore = primaryTrackerScore;
        this._status = status;

        this._target.descriptivePanel.headerText.text = this._languagePack.getText(this._status.headerLpKey);
        if (this._target.descriptivePanel.titleText) {
            this._target.descriptivePanel.titleText.text = this._languagePack.getText(this._status.titleLpKey);
        }
        this._target.descriptivePanel.bodyText.text = this._languagePack.getText(this._status.descriptionLpKey);

        const groupSpiritScore = this._trackerScores.find(trackerScore => trackerScore.id === `groupSpirit`);
        this._target.groupSpiritTextField.text = `${groupSpiritScore.getRoundedPercentage()}% ` + this._languagePack.getText(`endOfGame.scorePanel.groupSpirit`);;

        const trustScore = this._trackerScores.find(trackerScore => trackerScore.id === `trust`);
        this._target.trustTextField.text = `${trustScore.getRoundedPercentage()}% ` + this._languagePack.getText(`endOfGame.scorePanel.trust`);

        this._target.scoreMeter.gotoAndStop(this._primaryTrackerScore.getRoundedPercentage());
    }

    hideInstant() {
        this._target.alpha = 0;
        this._target.confetti.alpha = 0;
    }

    async show() {
        createjs.Tween.get(this._target)
            .to({ alpha: 1, }, 650, createjs.Ease.sineInOut)
            .toPromise();

        await this._animateScoreMeter();

        this._soundManager.playSound(SoundType.END_OF_GAME_CONFETTI);
        await createjs.Tween.get(this._target.confetti)
            .to({ alpha: 1, }, 1050, createjs.Ease.sineInOut)
            .toPromise();

        this._soundManager.playSound(SoundType.END_OF_GAME_COACH);
        this._target.coach.play();
    }

    async _animateScoreMeter() {
        this._target.scoreMeter.x = this._target.scoreMeterStart.x;
        this._target.scoreMeter.y = this._target.scoreMeterStart.y;
        this._target.scoreMeter.scale = this._target.scoreMeterStart.scale;

        await createjs.Tween.get(this._target.scoreMeter)
            .to({
                x: this._target.scoreMeter.initialX,
                y: this._target.scoreMeter.initialY,
                scale: this._target.scoreMeter.initialScale,
            }, 650, createjs.Ease.sineInOut)
            .toPromise();
    }

    _handleDownloadPersonalReportButton() {
        this._downloadPersonalReportButton.skin.disabled.downloadPdf.play();
        this._downloadPersonalReportButton.setEnabled(false);
        this.dispatch(PersonalReportEvent.GENERATE);
    }

    _handleDownloadPersonalReportComplete() {
        this._downloadPersonalReportButton.setEnabled(true);

        this._showShareMessage();
    }

    _showShareMessage() {
        const systemMessage = new SystemMessageBuilder()
            .setTitle(this._languagePack.getText(`endOfGame.shareMessage.title`))
            .setDescription(this._languagePack.getText(`endOfGame.shareMessage.body`))
            .addOption({
                text: this._languagePack.getText(`endOfGame.shareMessage.copyMailButtonLabel`),
                callback: () => {
                    navigator.clipboard.writeText(this._languagePack.getText(`endOfGame.shareMessage.mail`));
                },
                preventClose: true,
            })
            .addOption({
                text: this._languagePack.getText(`endOfGame.shareMessage.confirmationButtonLabel`),
            })
            .build();

        this.showSystemMessage(systemMessage);
    }

    _setLanguagePackTexts() {
        this._target.scoreMeter.teamMeterText.text = this._languagePack.getText(`endOfGame.generic.teamScoreMeter`);
        this._target.descriptivePanel.hintText.text = this._languagePack.getText(`endOfGame.overviewPanel.hint`);
    }
}